import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import { useCart } from "../context/CartContext";
import { clearCheckoutRecovery, clearPendingPayment } from "../common/checkoutStorage";

const BASE_URL = process.env.REACT_APP_API_URL;

const STATUS_LABEL = {
  pending: { text: "Pending", cls: "bg-yellow-50 text-yellow-500 border-yellow-200" },
  deposit_paid: { text: "Deposit Paid", cls: "bg-blue-50   text-blue-500   border-blue-200" },
  pending_balance: { text: "Balance Due", cls: "bg-orange-50 text-orange-500 border-orange-200" },
  confirmed: { text: "Confirmed", cls: "bg-green-50  text-green-500  border-green-200" },
  cancelled: { text: "Cancelled", cls: "bg-gray-100  text-gray-400   border-gray-200" },
};

function authHeader() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatKRW(n) {
  return `₩${Number(n).toLocaleString("ko-KR")}`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [toast, setToast] = useState(null);
  const highlightCode = searchParams.get("booking");
  const { clearCart } = useCart();

  useEffect(() => {
    const booking = searchParams.get("booking");
    const cart = searchParams.get("cart");
    const error = searchParams.get("error");

    if (booking) {
      clearCheckoutRecovery();
      setToast(`✅ Booking confirmed! Code: ${booking}`);
      setTimeout(() => setToast(null), 4000);
    }
    if (cart) {
      clearCheckoutRecovery();
      clearCart();  // 장바구니 비우기
      setToast("✅ All bookings confirmed! Check your bookings below.");
      setTimeout(() => setToast(null), 4000);
    }
    if (error === "payment_failed") {
      clearPendingPayment();
      setToast("❌ Payment failed. Please try again.");
      setTimeout(() => setToast(null), 4000);
    }
  }, []);

  // 수정 — 로그인 체크 useEffect 분리
  useEffect(() => {
    if (!user) {
      // 결제 완료 후 돌아온 경우 쿼리 파라미터 보존
      const params = searchParams.toString();
      navigate(`/login${params ? `?redirect=/orders&${params}` : "?redirect=/orders"}`);
      return;
    }
    // 예약 목록 fetch
    fetch(`${BASE_URL}/bookings`, { headers: authHeader() })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load bookings");
        return r.json();
      })
      .then(setBookings)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (!highlightCode || loading) return;
    const el = document.getElementById(highlightCode);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [loading, highlightCode]);

  async function handleCancel(bookingId) {
    if (!window.confirm("Are you sure you want to cancel this booking?\nDeposit will be refunded.")) return;
    try {
      const res = await fetch(`${BASE_URL}/payment/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ bookingId }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setBookings((prev) =>
        prev.map((b) => b.id === bookingId ? { ...b, status: "cancelled" } : b)
      );
      setToast("✅ Booking cancelled. Deposit will be refunded.");
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header dark />

      <div className="max-w-screen-md mx-auto px-6 pt-[100px] pb-16">
        <h1 className="text-[24px] font-bold text-gray-900 mb-6">My Bookings</h1>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-[20px] p-6 animate-pulse">
                <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
                <div className="h-3 w-48 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-24 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-[15px]">⚠️ {error}</p>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && bookings.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-[32px] mb-3">🗓️</p>
            <p className="text-[15px] font-medium">No bookings yet</p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 px-5 py-2 rounded-full bg-[#E8876A] text-white text-[13px] font-semibold hover:bg-[#D96F52] transition-colors"
            >
              Explore Tours
            </button>
          </div>
        )}

        {/* Booking list */}
        {!loading && !error && bookings.length > 0 && (
          <div className="flex flex-col gap-4">
            {bookings.map((b) => {
              const status = STATUS_LABEL[b.status] ?? STATUS_LABEL.pending;
              const isHighlight = b.booking_code === highlightCode;

              return (
                <div
                  key={b.id}
                  id={b.booking_code}
                  className={`bg-white rounded-[20px] border shadow-sm overflow-hidden transition-all ${isHighlight
                    ? "border-[#E8876A] ring-2 ring-[#E8876A]/30"
                    : "border-gray-100"
                    }`}
                >
                  <div className="p-5 flex flex-col gap-3">

                    {/* Tour name + status */}
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-[15px] font-bold text-gray-900 leading-snug">{b.title}</h2>
                      <span className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${status.cls}`}>
                        {status.text}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-x-5 gap-y-1">
                      <Info icon="📅" text={formatDate(b.date)} />
                      <Info icon="👤" text={`${b.persons} person(s)`} />
                      <Info icon="💳" text={formatKRW(b.total)} />
                    </div>

                    {/* Booking ID + date */}
                    <p className="text-[11px] text-gray-300">
                      Booking {b.booking_code} · Booked on {formatDate(b.created_at)}
                    </p>
                  </div>

                  {/* Cancel button */}
                  {b.status !== "cancelled" && (
                    <div className="border-t border-gray-100 px-5 py-3">
                      <button
                        onClick={() => handleCancel(b.id)}
                        className="text-[13px] text-gray-400 hover:text-red-400 transition-colors"
                      >
                        Cancel Booking
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#2C2C2A] text-white px-5 py-2.5 rounded-full text-[13px] z-[2000] whitespace-nowrap pointer-events-none">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ icon, text }) {
  return (
    <span className="flex items-center gap-1 text-[13px] text-gray-500">
      <span>{icon}</span>{text}
    </span>
  );
}
